'use strict';

import DiceBox from '@3d-dice/dice-box';

window.dndNextLevel = window.dndNextLevel || {};
dndNextLevel.dicetray = dndNextLevel.dicetray || {};

dndNextLevel.dicetray.state = {
    config: null,
    diceBox: null,
    immersiveReady: false,
    rollPending: false,
    currentRoll: null,
};

dndNextLevel.dicetray.cacheElements = function () {
    dndNextLevel.dicetray.headerSubtitle = document.getElementById('headerSubtitle');
    dndNextLevel.dicetray.title = document.getElementById('beholderText');
    dndNextLevel.dicetray.notationInput = document.getElementById('dice-notation');
    dndNextLevel.dicetray.rollButton = document.getElementById('roll-dice');
    dndNextLevel.dicetray.saveRollButton = document.getElementById('save-roll');
    dndNextLevel.dicetray.clearHistoryButton = document.getElementById('clear-history');
    dndNextLevel.dicetray.quickRollButtons = document.querySelectorAll('.quick-roll');
    dndNextLevel.dicetray.rollStatus = document.getElementById('roll-status');
    dndNextLevel.dicetray.modePill = document.getElementById('mode-pill');
    dndNextLevel.dicetray.modeDescription = document.getElementById('mode-description');
    dndNextLevel.dicetray.rollTotal = document.getElementById('roll-total');
    dndNextLevel.dicetray.normalizedNotation = document.getElementById('normalized-notation');
    dndNextLevel.dicetray.rollModifier = document.getElementById('roll-modifier');
    dndNextLevel.dicetray.rollSummary = document.getElementById('roll-summary');
    dndNextLevel.dicetray.rollBreakdown = document.getElementById('roll-breakdown');
    dndNextLevel.dicetray.rollHistory = document.getElementById('roll-history');
    dndNextLevel.dicetray.rollHistoryEmpty = document.getElementById('roll-history-empty');
};

dndNextLevel.dicetray.coreFunctionality = async function () {
    dndNextLevel.dicetray.cacheElements();
    dndNextLevel.dicetray.bindEvents();
    dndNextLevel.dicetray.initializeWorkbenchViews();

    try {
        await dndNextLevel.dicetray.getConfiguration();
        await dndNextLevel.dicetray.initializePreferredMode();
    } catch (error) {
        console.warn('Dice Tray initialization fallback:', error);
        dndNextLevel.dicetray.setMode(false, 'Dice Tray could not start the 3D renderer. Instant mode is active.');
        dndNextLevel.dicetray.setStatus('Instant mode ready. Rolls will resolve through the server.');
    }

    const restored = dndNextLevel.dicetray.recentAdapter?.consumeRestore();
    if (restored) {
        dndNextLevel.dicetray.renderRoll(restored);
        dndNextLevel.dicetray.appendHistory(restored);
        dndNextLevel.dicetray.focusResult();
        dndNextLevel.dicetray.setStatus('Saved roll restored.');
    }
};

dndNextLevel.dicetray.setWorkbenchView = function (view) {
    document.querySelectorAll('[data-workbench-view]').forEach(panel => {
        panel.classList.toggle('hidden', panel.dataset.workbenchView !== view);
    });
    document.querySelectorAll('[data-workbench-view-target]').forEach(button => {
        const selected = button.dataset.workbenchViewTarget === view;
        button.setAttribute('aria-pressed', String(selected));
        button.classList.toggle('bg-slate-900', selected);
        button.classList.toggle('text-white', selected);
        button.classList.toggle('border', !selected);
        button.classList.toggle('border-slate-300', !selected);
        button.classList.toggle('dark:border-zinc-600', !selected);
    });
};

dndNextLevel.dicetray.initializeWorkbenchViews = function () {
    document.querySelectorAll('[data-workbench-view-target]').forEach(button => {
        button.addEventListener('click', () => {
            dndNextLevel.dicetray.setWorkbenchView(button.dataset.workbenchViewTarget);
        });
    });
};

dndNextLevel.dicetray.bindEvents = function () {
    dndNextLevel.dicetray.rollButton.addEventListener('click', () => dndNextLevel.dicetray.requestRoll());
    dndNextLevel.dicetray.saveRollButton?.addEventListener('click', () => dndNextLevel.dicetray.saveCurrentRoll());
    dndNextLevel.dicetray.clearHistoryButton.addEventListener('click', dndNextLevel.dicetray.clearHistory);

    dndNextLevel.dicetray.notationInput.addEventListener('keydown', event => {
        if (event.key === 'Enter') {
            event.preventDefault();
            dndNextLevel.dicetray.requestRoll();
        }
    });

    dndNextLevel.dicetray.quickRollButtons.forEach(button => {
        button.addEventListener('click', () => {
            dndNextLevel.dicetray.notationInput.value = button.dataset.notation;
            dndNextLevel.dicetray.requestRoll(button.dataset.notation);
        });
    });

};

dndNextLevel.dicetray.getConfiguration = async function () {
    const response = await fetch('/api/dicetray/config', {credentials: 'same-origin'});
    if (!response.ok) {
        throw new Error('Failed to load Dice Tray config');
    }

    dndNextLevel.dicetray.state.config = await response.json();

    document.title = dndNextLevel.dicetray.state.config.pageTitle;
    if (dndNextLevel.dicetray.headerSubtitle) {
        dndNextLevel.dicetray.headerSubtitle.textContent = dndNextLevel.dicetray.state.config.headerSubtitle;
    }
    if (dndNextLevel.dicetray.title) {
        dndNextLevel.dicetray.title.textContent = dndNextLevel.dicetray.state.config.beholderText;
    }

    if (!dndNextLevel.dicetray.notationInput.value && dndNextLevel.dicetray.state.config.presets.length > 0) {
        dndNextLevel.dicetray.notationInput.value = dndNextLevel.dicetray.state.config.presets[0];
    }
};

dndNextLevel.dicetray.prefersReducedMotion = function () {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
};

dndNextLevel.dicetray.initializePreferredMode = async function () {
    if (dndNextLevel.dicetray.prefersReducedMotion()) {
        dndNextLevel.dicetray.setMode(false, 'Reduced motion is enabled. Instant mode is active.');
        dndNextLevel.dicetray.setStatus('Instant mode ready. Rolls will resolve through the server.');
        return;
    }
    await dndNextLevel.dicetray.initializeDiceBox();
};

dndNextLevel.dicetray.initializeDiceBox = async function () {
    try {
        dndNextLevel.dicetray.state.diceBox = new DiceBox({
            container: '#dice-box',
            assetPath: dndNextLevel.dicetray.state.config.assetPath,
            scale: window.innerWidth < 768 ? 8.25 : 9.75,
            theme: 'diceOfRolling',
            themeColor: '#2e8555',
            offscreen: false,
        });

        await dndNextLevel.dicetray.state.diceBox.init();
        if (!dndNextLevel.dicetray.hasRenderableSurface()) {
            throw new Error('Dice Tray initialized without attaching a visible canvas.');
        }
        dndNextLevel.dicetray.state.immersiveReady = true;
        dndNextLevel.dicetray.setMode(true, '3D dice are ready. Rolls animate in the tray and fall back instantly if rendering fails.');
        dndNextLevel.dicetray.setStatus('Dice Tray ready. Roll something dramatic.');
    } catch (error) {
        console.warn('Dice Box init fallback:', error);
        dndNextLevel.dicetray.state.immersiveReady = false;
        dndNextLevel.dicetray.setMode(false, '3D dice are unavailable because the renderer did not attach a visible canvas. Instant mode is active.');
        dndNextLevel.dicetray.setStatus('Instant mode ready. Rolls will resolve through the server.');
    }
};

dndNextLevel.dicetray.hasRenderableSurface = function () {
    const attachedCanvas = document.querySelector('#dice-box canvas');
    return attachedCanvas instanceof HTMLCanvasElement && attachedCanvas.isConnected;
};

dndNextLevel.dicetray.requestRoll = async function (notationOverride) {
    const notation = (notationOverride || dndNextLevel.dicetray.notationInput.value || '').trim();
    if (!notation || dndNextLevel.dicetray.state.rollPending) {
        return;
    }

    dndNextLevel.dicetray.notationInput.value = notation;
    dndNextLevel.dicetray.setPending(true);

    try {
        if (dndNextLevel.dicetray.state.immersiveReady) {
            const payload = await dndNextLevel.dicetray.rollImmersive(notation);
            dndNextLevel.dicetray.renderRoll(payload);
            dndNextLevel.dicetray.appendHistory(payload);
        } else {
            const payload = await dndNextLevel.dicetray.rollInstant(notation);
            dndNextLevel.dicetray.renderRoll(payload);
            dndNextLevel.dicetray.appendHistory(payload);
            dndNextLevel.dicetray.setStatus('Instant roll complete.');
        }
        dndNextLevel.dicetray.focusResult();
    } catch (error) {
        console.error('Dice Tray roll failed:', error);
        dndNextLevel.dicetray.showError(error.message || 'Roll failed.');
    } finally {
        dndNextLevel.dicetray.setPending(false);
    }
};

dndNextLevel.dicetray.focusResult = function () {
    dndNextLevel.dicetray.setWorkbenchView('result');
    document.getElementById('dice-tray-result')?.focus?.({preventScroll: true});
};

dndNextLevel.dicetray.rollImmersive = async function (notation) {
    try {
        const parsedNotation = dndNextLevel.dicetray.parseExpression(notation);
        const results = await dndNextLevel.dicetray.state.diceBox.roll(
            parsedNotation.groups.map(group => group.immersiveNotation)
        );
        const payload = dndNextLevel.dicetray.normalizeDiceBoxResults(notation, parsedNotation, results);
        dndNextLevel.dicetray.setStatus('3D roll complete.');
        return payload;
    } catch (error) {
        console.warn('Dice Tray immersive roll fell back to instant mode for this roll.', error);
        const payload = await dndNextLevel.dicetray.rollInstant(notation);
        dndNextLevel.dicetray.setStatus('3D roll fallback complete.');
        return payload;
    }
};

dndNextLevel.dicetray.rollInstant = async function (notation) {
    const response = await fetch(dndNextLevel.dicetray.state.config.actionUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({notation}),
    });

    const admissionError = dndNextLevel.admission?.checkResponse(response);
    if (admissionError) throw new Error(admissionError);
    const payload = await response.json();
    if (!response.ok) {
        throw new Error(payload.error || 'Dice Tray could not resolve that roll.');
    }

    return payload;
};

dndNextLevel.dicetray.parseExpression = function (notation) {
    const invalidNotationMessage = 'Use simple dice notation like d20 or 2d6+3.';
    const normalizedInput = (notation || '').trim().replace(/\s+/g, '').toLowerCase();
    const termPattern = /([+-]?)(\d*d(?:100|20|12|10|8|6|4)|\d+)/g;
    const dicePattern = /^(\d*)d(100|20|12|10|8|6|4)$/;

    if (!normalizedInput) {
        throw new Error(invalidNotationMessage);
    }

    const groups = [];
    let modifier = 0;
    let totalDiceCount = 0;
    let cursor = 0;
    let match;

    while ((match = termPattern.exec(normalizedInput)) !== null) {
        if (match.index !== cursor) {
            throw new Error(invalidNotationMessage);
        }

        cursor = termPattern.lastIndex;
        const sign = match[1];
        const token = match[2];

        if (token.includes('d')) {
            if (sign === '-') {
                throw new Error(invalidNotationMessage);
            }

            const diceMatch = token.match(dicePattern);
            if (!diceMatch) {
                throw new Error(invalidNotationMessage);
            }

            const quantity = diceMatch[1] ? Number(diceMatch[1]) : 1;
            const sides = Number(diceMatch[2]);
            totalDiceCount += quantity;

            if (totalDiceCount > 20) {
                throw new Error('Dice Tray supports up to 20 dice per roll.');
            }

            groups.push({
                groupId: groups.length,
                quantity,
                sides,
                normalizedNotation: dndNextLevel.dicetray.buildGroupNotation(quantity, sides),
                immersiveNotation: `${quantity}d${sides}`,
            });
            continue;
        }

        const constant = Number(token);
        modifier += sign === '-' ? -constant : constant;
    }

    if (cursor !== normalizedInput.length || groups.length === 0) {
        throw new Error(invalidNotationMessage);
    }

    if (Math.abs(modifier) > 100) {
        throw new Error('Dice Tray supports modifiers between -100 and 100.');
    }

    return {
        requestedNotation: notation.trim(),
        normalizedNotation: dndNextLevel.dicetray.buildNormalizedExpression(groups, modifier),
        groups,
        modifier,
        totalDiceCount,
    };
};

dndNextLevel.dicetray.normalizeDiceBoxResults = function (notation, parsedNotation, results) {
    const dice = Array.isArray(results) ? results : [];
    if (dice.length !== parsedNotation.totalDiceCount) {
        throw new Error('Dice Tray could not resolve that roll.');
    }

    const groups = parsedNotation.groups.map(group => {
        const groupDice = dice
            .filter(die => Number(die.groupId) === group.groupId)
            .sort((left, right) => Number(left.rollId) - Number(right.rollId));
        const rolls = groupDice
            .map(die => Number(die.value))
            .filter(value => !Number.isNaN(value));

        if (rolls.length !== group.quantity) {
            throw new Error('Dice Tray could not resolve that roll.');
        }

        const subtotal = rolls.reduce((sum, value) => sum + value, 0);
        return {
            groupId: group.groupId,
            quantity: group.quantity,
            sides: group.sides,
            normalizedNotation: group.normalizedNotation,
            rolls,
            subtotal,
        };
    });

    const rolls = groups.flatMap(group => group.rolls);
    const normalizedDice = dice.map(die => ({
        groupId: Number(die.groupId),
        rollId: Number(die.rollId),
        sides: Number(String(die.sides).replace(/\D/g, '')),
        theme: die.theme || 'default',
        themeColor: die.themeColor || '#2e8555',
        value: Number(die.value),
    }));
    const subtotal = rolls.reduce((sum, value) => sum + value, 0);
    const total = subtotal + parsedNotation.modifier;

    return {
        requestedNotation: notation,
        normalizedNotation: parsedNotation.normalizedNotation,
        quantity: groups.length === 1 ? groups[0].quantity : parsedNotation.totalDiceCount,
        sides: groups.length === 1 ? groups[0].sides : 0,
        modifier: parsedNotation.modifier,
        groups,
        rolls,
        dice: normalizedDice,
        subtotal,
        total,
        summary: dndNextLevel.dicetray.buildSummary(parsedNotation.normalizedNotation, groups, parsedNotation.modifier, total),
    };
};

dndNextLevel.dicetray.buildGroupNotation = function (quantity, sides) {
    const quantityText = quantity > 1 ? `${quantity}` : '';
    return `${quantityText}d${sides}`;
};

dndNextLevel.dicetray.buildNormalizedExpression = function (groups, modifier) {
    const notation = groups.map(group => group.normalizedNotation).join('+');
    if (modifier > 0) {
        return `${notation}+${modifier}`;
    }
    if (modifier < 0) {
        return `${notation}${modifier}`;
    }
    return notation;
};

dndNextLevel.dicetray.buildSummary = function (notation, groups, modifier, total) {
    const groupSummary = groups
        .map(group => `${group.normalizedNotation}=${JSON.stringify(group.rolls)}`)
        .join(', ');
    const modifierText = modifier === 0 ? '' : `, modifier=${dndNextLevel.dicetray.formatModifier(modifier)}`;
    return `Rolled ${notation} with ${groupSummary}${modifierText} for a total of ${total}`;
};

dndNextLevel.dicetray.formatModifier = function (modifier) {
    return modifier > 0 ? `+${modifier}` : `${modifier}`;
};

dndNextLevel.dicetray.renderRoll = function (payload) {
    const modifierText = dndNextLevel.dicetray.formatModifier(payload.modifier);
    dndNextLevel.dicetray.rollTotal.textContent = payload.total;
    dndNextLevel.dicetray.normalizedNotation.textContent = payload.normalizedNotation;
    dndNextLevel.dicetray.rollModifier.textContent = modifierText;
    dndNextLevel.dicetray.rollSummary.textContent = payload.summary;
    dndNextLevel.dicetray.state.currentRoll = payload;
    if (dndNextLevel.dicetray.saveRollButton) {
        dndNextLevel.dicetray.saveRollButton.disabled = false;
    }
    if (Array.isArray(payload.groups) && payload.groups.length > 0) {
        const groupBreakdown = payload.groups
            .map(group => `${group.normalizedNotation}: [${group.rolls.join(', ')}]`)
            .join(' | ');
        dndNextLevel.dicetray.rollBreakdown.textContent =
            `${groupBreakdown} | Dice subtotal: ${payload.subtotal} | Modifier: ${dndNextLevel.dicetray.formatModifier(payload.modifier)}`;
        return;
    }

    dndNextLevel.dicetray.rollBreakdown.textContent =
        payload.rolls.length > 0
            ? `Dice: [${payload.rolls.join(', ')}] | Subtotal: ${payload.subtotal} | Modifier: ${dndNextLevel.dicetray.formatModifier(payload.modifier)}`
            : 'No dice were rolled.';
};

dndNextLevel.dicetray.saveCurrentRoll = function () {
    const adapter = dndNextLevel.dicetray.recentAdapter || dndNextLevel.diceTrayRecentAdapter;
    const entry = adapter?.save(dndNextLevel.dicetray.state.currentRoll, window.localStorage);
    if (!entry) return false;
    dndNextLevel.dicetray.setStatus('Roll saved in recent creations.');
    return true;
};

dndNextLevel.dicetray.appendHistory = function (payload) {
    dndNextLevel.dicetray.rollHistoryEmpty.classList.add('hidden');

    const item = document.createElement('li');
    item.className = 'rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-slate-100';
    const breakdown = Array.isArray(payload.groups) && payload.groups.length > 0
        ? payload.groups.map(group => `${group.normalizedNotation}[${group.rolls.join(', ')}]`).join(' ')
        : `[${payload.rolls.join(', ')}]`;
    item.textContent = `${new Date().toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})} • ${payload.normalizedNotation} = ${payload.total} ${breakdown}`;

    dndNextLevel.dicetray.rollHistory.prepend(item);

    while (dndNextLevel.dicetray.rollHistory.children.length > 8) {
        dndNextLevel.dicetray.rollHistory.removeChild(dndNextLevel.dicetray.rollHistory.lastChild);
    }
};

dndNextLevel.dicetray.clearHistory = function () {
    dndNextLevel.dicetray.rollHistory.innerHTML = '';
    dndNextLevel.dicetray.rollHistoryEmpty.classList.remove('hidden');
    dndNextLevel.dicetray.rollSummary.textContent = 'No roll yet.';
    dndNextLevel.dicetray.rollBreakdown.textContent = 'Roll results and totals appear here after the tray lands.';
    dndNextLevel.dicetray.rollTotal.textContent = '-';
    dndNextLevel.dicetray.normalizedNotation.textContent = '-';
    dndNextLevel.dicetray.rollModifier.textContent = '+0';
    dndNextLevel.dicetray.state.currentRoll = null;
    if (dndNextLevel.dicetray.saveRollButton) {
        dndNextLevel.dicetray.saveRollButton.disabled = true;
    }

    if (dndNextLevel.dicetray.state.diceBox) {
        dndNextLevel.dicetray.state.diceBox.clear();
    }
};

dndNextLevel.dicetray.setPending = function (isPending) {
    dndNextLevel.dicetray.state.rollPending = isPending;
    dndNextLevel.dicetray.rollButton.disabled = isPending;
    dndNextLevel.dicetray.rollButton.textContent = isPending ? 'Rolling...' : 'Roll';
};

dndNextLevel.dicetray.setStatus = function (message) {
    dndNextLevel.dicetray.rollStatus.textContent = message;
};

dndNextLevel.dicetray.setMode = function (immersiveReady, description) {
    dndNextLevel.dicetray.state.immersiveReady = immersiveReady;
    dndNextLevel.dicetray.modePill.textContent = immersiveReady ? '3D' : 'Instant';
    dndNextLevel.dicetray.modePill.className = immersiveReady
        ? 'rounded-full border border-cyan-400/60 bg-cyan-500/15 px-4 py-2 text-sm font-semibold uppercase tracking-[0.2em] text-cyan-200'
        : 'rounded-full border border-amber-400/60 bg-amber-500/15 px-4 py-2 text-sm font-semibold uppercase tracking-[0.2em] text-amber-200';
    dndNextLevel.dicetray.modeDescription.textContent = description;
};

dndNextLevel.dicetray.showError = function (message) {
    dndNextLevel.dicetray.rollSummary.textContent = 'Roll failed.';
    dndNextLevel.dicetray.rollBreakdown.textContent = message;
    dndNextLevel.dicetray.setStatus(message);
};

document.addEventListener('DOMContentLoaded', function () {
    dndNextLevel.dicetray.coreFunctionality();
});
